<template>
  <CompaniesAnalysisLayout
    :not-available-type="!surveysLoading ? notAvailableType : null"
    :company-uuid="companyUuid"
    :is-more-than-editor="showMoreThanEditorAuthority"
  >
    <template #header>
      <div v-if="selectedSurvey && !surveysLoading" class="surveyForm">
        <span class="surveyForm__label">
          {{ $t('pages.CompaniesCustomSurveyAnswers.selectSurvey') }}
        </span>
        <div class="surveyForm__input" :style="{ width: surveyFormWidth + 'px' }">
          <KkSuggestField
            id="survey"
            :value="inputSurveyName"
            :suggests="selectableSurveys"
            @select-suggest="changeSurvey"
            @close-suggest="handleSuggestFieldClose"
            @focus-field="handleSuggestFieldFocus"
            @input-field="handleInputSurveyName"
          >
            <template #suggest="slotProps">
              <span v-text="slotProps.item.name" />
            </template>
          </KkSuggestField>
        </div>
      </div>
    </template>
    <template #overview>
      <Loading v-if="loading" />
      <div v-else-if="selectedSurvey" class="surveyInfo">
        <span class="surveyInfo__title">{{ selectedSurvey.name }}</span>
        <div class="surveyInfo__status">
          <p class="statusMsg">
            <StatusBadge :status="extractStatusNumber(selectedSurvey.status)" />
            {{ $t('pages.CompaniesSurveys.publishedStatus.' + selectedSurvey.status) }}
          </p>
        </div>
        <div v-if="selectedSurvey.status !== 'unpublished'" class="surveyInfo__period">
          <div>{{ $t('labels.date.start') }}：{{ dateFormat(selectedSurvey.publishedAt) }}</div>
          <div class="surveyInfo__period--endDate">{{ $t('labels.date.end') }}：{{ dateFormat(selectedSurvey.closedAt) }}</div>
        </div>
        <div class="surveyInfo__total">
          <span class="surveyInfo__title">{{ $t('pages.companies.analysis.surveys.totalNumberResponse', { number: numberLocaleString(selectedSurvey.answerCount) }) }}</span>
          <div class="surveyInfo__preview">
            <button type="button" class="surveyInfo__preview__link" @click="openPreview(selectedSurvey.id)">
              {{ $t('pages.companies.analysis.surveys.viewSurveyForm') }}
            </button>
          </div>
        </div>
        <Modal v-if="isPreview" width="375px" @close="closePreview">
          <Loading v-if="surveyLoading" />
          <SurveyInputFormPreview v-else :survey="survey" />
        </Modal>
      </div>
    </template>

    <template #graph>
      <Loading v-if="loading" />
      <div v-else>
        <Info>
          {{ $t('pages.companies.analysis.surveys.info') }}
        </Info>
        <div class="graphHeader">
          <div class="displayGraph">
            <span class="u-fwB">
              {{ $t('pages.companies.analysis.surveys.displayGraph') }}：
            </span>
            <div v-if="!analysisSurveyLoading && isDataEnough" class="c-select">
              <select v-model="displayQuestionId">
                <option :value="null" disabled>{{ $t('labels.placeholder.select') }}</option>
                <option v-for="question in displayQuestions" :key="question.name" :value="question.id">
                  {{ 'Q' + question.position + '. ' + truncateName(question.name) }}
                </option>
              </select>
            </div>
          </div>
          <div class="headerButtons">
            <DatePicker
              :initial-dates="period"
              opens="left"
              @input="changePeriod"
            />
            <KkButton
              v-if="canExportAuthority('analysis/surveys') && providedFeaturesCsvExport"
              class="csvButton"
              color="secondary"
              height="46px"
              :font-bold="false"
              :text="$t('labels.csvDownload')"
              :disabled="loading"
              @click.prevent="setExportCsv"
            />
          </div>
        </div>
        <button v-show="canDisplayRatio" class="c-button__item c-button__item--outline changeDisplayGraphTypeBtn" @click="toggleGraphDisplayType">
          {{ graphDisplayType === 'count' ? $t('pages.companies.analysis.surveys.displayByRatio') : $t('pages.companies.analysis.surveys.displayByCount') }}
        </button>
        <div v-if="!analysisSurveyLoading && isDataEnough && displayQuestion" class="graphs">
          <ChartHorizontalBar
            v-show="chartData.labels.length !== 0"
            :chart-data="chartData"
            :options="optionsForChartHorizontalBar"
            :styles="{ 'height': `${chartHeight}px`, 'position': 'relative', 'transition': '.4s' }"
          />
        </div>
      </div>
    </template>

    <template #terms>
      <CompaniesAnalysisCrossSearch
        v-if="!axesLoading"
        :disabled="analysisSurveyLoading"
        :cross-search-field="crossAnalysisAxis"
        :current-page-type="analysisAxis"
        :page-types="axesExceptSite"
        @set-page-type="changeAnalysisAxis"
        @set-cross-search-field="changeCrossAnalysisAxis"
      />
    </template>

    <!-- 現在の表示リスト / 表示リストのリセット -->
    <template #reset>
      <ul v-show="individualAnalysisName !== ''" class="c-links">
        <li class="c-links__item">
          <strong @click="changeIndividualAnalysisField(null)">{{ $t('pages.companies.analysis.individual.reset') }}</strong> | {{ $t('pages.companies.analysis.individual.listTitle', { name: individualAnalysisName }) }}
        </li>
      </ul>
    </template>

    <template #table>
      <CompaniesAnalysisSurveysTable
        :cross-search-fields="crossSearchFields"
        :items="analysisSurveyDataList"
        :search-items="displayGraphIds"
        :loading="loading"
        :static-heads="questionsForTable"
        :start-date="dateFormat(period.startDate)"
        :end-date="dateFormat(period.endDate)"
        :survey-id="selectedSurveyId"
        :show-review-link="true"
        @change-display-graphs="changeDisplayGraph"
        @change-individual-analysis-field="changeIndividualAnalysisField"
      />
    </template>
  </CompaniesAnalysisLayout>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState, mapGetters } from 'vuex'
import { colors, crossAnalysisTypes, npsList, decimalPlace } from '../../../modules/analysis'
import { roundDecimal } from '../../../modules/decimalCalc'
import companiesNotAvailableTypeMixin from '../../../mixins/companiesNotAvailableTypeMixin'
import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout'
import CompaniesAnalysisCrossSearch from '../../../components/CompaniesAnalysis/CrossSearch.vue'
import CompaniesAnalysisSurveysTable from '../../../components/CompaniesAnalysis/Surveys/Table'
import Loading from '../../../components/Loading'
import Info from '../../../components/Info'
import DatePicker from '../../../components/DatePicker'
import ChartHorizontalBar from '../../../components/ChartHorizontalBar'
import { KkSuggestField } from '../../../components/ui'
import { KkButton } from '../../../components/base'
import StatusBadge from '../../../components/StatusBadge'
import { toNumberLocaleString } from '../../../modules/localeString'
import Modal from '../../../components/Modal'
import SurveyInputFormPreview from '../../../components/SurveyInputFormPreview'

export default {
  name: 'CompaniesAnalysisSurveys',
  components: {
    ChartHorizontalBar,
    Info,
    Loading,
    CompaniesAnalysisLayout,
    CompaniesAnalysisCrossSearch,
    CompaniesAnalysisSurveysTable,
    DatePicker,
    KkSuggestField,
    KkButton,
    StatusBadge,
    Modal,
    SurveyInputFormPreview,
  },
  mixins: [companiesNotAvailableTypeMixin],
  emits: ['update-title'],
  data() {
    return {
      selectedSurveyId: Number(this.$route.query.custom_survey_id) || null,
      crossAnalysisAxis: null, // クロス分析軸
      analysisAxis: crossAnalysisTypes[0], // 分析軸
      individualAnalysisField: null,
      displayGraphIds: [],
      displayGraphDataList: [],
      displayQuestionId: null,
      graphDisplayType: 'count', // 'count' or 'ratio'
      cacheInputSurveyName: '',
      inputSurveyName: '',
      surveyFormWidth: 200,
      isPreview: false,
    }
  },
  computed: {
    ...mapState({
      surveysLoading: state => state.companiesCustomSurveySurveys.loading,
      // NOTE: 言語別はこの分析画面で利用しないため削除
      axes: state => state.companiesAnalysisAxes.data.filter(v => !['locale'].includes(v.id)),
      axesLoading: state => state.companiesAnalysisAxes.loading,
      survey: state => state.companiesCustomSurvey.data,
      surveyLoading: state => state.companiesCustomSurvey.loading,
      analysisSurveyLoading: state => state.companiesAnalysisSurveys.loading,
      analysisSurveyDataList: state => state.companiesAnalysisSurveys.data,
      period: state => state.filters.period,
    }),
    ...mapGetters({
      surveys: 'companiesCustomSurveySurveys/analyzableSurveys',
      questionsForTable: 'companiesAnalysisSurveys/questionsForTable',
      displayQuestions: 'companiesAnalysisSurveys/displayQuestions',
      canExportAuthority: 'companiesAccessibleApi/canExportAuthority',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
    }),
    loading() {
      return this.surveysLoading || this.analysisSurveyLoading || this.axesLoading
    },
    selectedSurvey() {
      return this.surveys.find(survey => survey.id === this.selectedSurveyId)
    },
    selectableSurveys() {
      return this.surveys.filter(survey => survey.name.includes(this.inputSurveyName))
    },
    isDataEnough() {
      return this.analysisSurveyDataList.length !== 0
    },
    axesExceptSite() {
      return this.axes.filter(v => v.id !== 'site')
    },
    /**
     * @return string
     */
    companyUuid() {
      return this.$route.params.company_uuid
    },
    answeredAtTerm() {
      return moment(this.period.startDate).format('YYYY/MM/DD')
        + ' - '
        + moment(this.period.endDate).format('YYYY/MM/DD')
    },
    query() {
      return {
        analysisAxis: this.analysisAxis.fields,
        crossAnalysisAxis: this.crossAnalysisAxis?.fields,
        answeredAtTerm: this.answeredAtTerm,
        analysisAxisId: this.individualAnalysisField?.id,
      }
    },
    requestParams() {
      return { query: this.query, id: this.selectedSurveyId }
    },
    individualAnalysisName() {
      return this.individualAnalysisField ? this.individualAnalysisField.name : ''
    },
    crossSearchFields() {
      const arr = [this.analysisAxis]
      if (this.crossAnalysisAxis) {
        arr.push(this.crossAnalysisAxis)
      }
      return arr
    },
    displayQuestion() {
      return this.displayQuestions.find(question => question.id === this.displayQuestionId)
    },
    canDisplayRatio() {
      return this.displayQuestion?.type === 'radio' || this.displayQuestion?.type === 'nps'
    },
    optionsForChartHorizontalBar() {
      const x = {
        min: 0,
        ticks: {
          precision: 0
        },
        stacked: this.canDisplayRatio,
      }
      if (this.canDisplayRatio && this.graphDisplayType === 'ratio') {
        x.max = 100
      }
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x,
          y: {
            stacked: this.canDisplayRatio,
          },
        },
        plugins: {
          legend: {
            position: 'top'
          },
          tooltip: {
            enabled: true,
            mode: 'nearest',
            callbacks: {
              label: this.getTooltipLabel
            }
          },
        },
      }
    },
    npsChartDatasets() {
      return npsList.map(nps => {
        return {
          data: this.calcNpsChartData(nps),
          backgroundColor: nps.color,
          label: this.$t('models.survey.answerOption.nps.' + nps.id)
        }
      })
    },
    chartDatasets() {
      return this.displayQuestion.options.map((option, key) => {
        return {
          data: this.calcChartData(option),
          backgroundColor: colors[key],
          label: option.name
        }
      })
    },
    chartData() {
      return {
        labels: this.displayGraphDataList.map(data => data.fields.map(field => field.name).join(' / ')),
        datasets: this.displayQuestion.type === 'nps' ? this.npsChartDatasets : this.chartDatasets
      }
    },
    chartHeight() {
      if (this.canDisplayRatio) {
        const unit = 50
        if (this.displayGraphDataList.length === 1) return unit + 40
        return this.displayGraphDataList.length * unit
      }
      const unit = 30
      return this.displayGraphDataList.length * this.displayQuestion.options.length * unit
    },
    crossSearchField() {
      return null
    },
    pageType() {
      return null
    },
  },
  async created() {
    await Promise.all([
      this.readCompaniesAnalysisAxes(),
      this.readCustomSurveys()
    ])
    if (this.notAvailableType) return
    if (this.selectedSurveyId == null) {
      this.selectedSurveyId = this.surveys[0].id
    }
    this.setInitSurveyForm()
    this.fetchSurvey()
  },
  methods: {
    ...mapActions({
      readCompaniesAnalysisAxes: 'companiesAnalysisAxes/readCompaniesAnalysisAxes',
      readCustomSurveys: 'companiesCustomSurveySurveys/readCustomSurveys',
      readCustomSurvey: 'companiesCustomSurvey/readCustomSurvey',
      readAnalysisCustomSurvey: 'companiesAnalysisSurveys/readAnalysisCustomSurvey',
      exportCsv: 'companiesAnalysisSurveys/exportCsv',
      setPeriod: 'filters/setPeriod',
    }),
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    dateFormat(date) {
      return date ? moment(date).format(this.$t('format.date.default')) : '-'
    },
    extractStatusNumber(status) {
      switch (status) {
      case 'published':
        return 1
      case 'unpublished':
        return 2
      case 'closed':
        return 3
      }
    },
    openPreview(id) {
      this.readCustomSurvey(id)
      this.isPreview = true
    },
    closePreview() {
      this.isPreview = false
    },
    truncateName(name) {
      if (name.length > 15) {
        return name.slice(0, 15) + '...'
      } else {
        return name
      }
    },
    setInitSurveyForm() {
      this.inputSurveyName = this.selectedSurvey?.name
      this.setInitSurveyFormWidth()
    },
    /**
     * アンケート選択フォームの初期サイズ設定
     * @return { Void }
     */
    setInitSurveyFormWidth() {
      var width = 200
      this.surveys.forEach(survey => {
        if (survey.name.length * 15 > width) width = survey.name.length * 15
      })

      this.surveyFormWidth = width
    },
    changeSurvey(suggest) {
      const selectedSurvey = this.surveys.find(s => s.id === suggest.suggest.id)
      this.inputSurveyName = selectedSurvey.name
      if (this.selectedSurveyId.id === selectedSurvey.id) return

      this.selectedSurveyId = selectedSurvey.id
      this.displayGraphIds = []
      this.fetchSurvey()
      this.$router.push({
        query: {
          custom_survey_id: this.selectedSurveyId,
        }
      })
    },
    async fetchSurvey() {
      await this.readAnalysisCustomSurvey(this.requestParams)
      if(this.displayGraphIds.length === 0) {
        this.changeDisplayGraph([this.analysisSurveyDataList[0].id])
        this.displayQuestionId = this.displayQuestions[0].id
      } else {
        this.changeDisplayGraph(this.displayGraphIds)
      }
    },
    /**
     * テーブル表示リストの変更
     * @param  { String } value
     * @return { Void }
     */
    changeIndividualAnalysisField(value) {
      if (this.analysisSurveyLoading) return
      this.individualAnalysisField = value
      this.fetchSurvey()
    },
    /**
     * 分析軸の変更
     * @param  { String } value
     * @return { Void }
     */
    async changeAnalysisAxis(value) {
      if (this.analysisSurveyLoading) return
      this.individualAnalysisField = null
      this.analysisAxis = value
      this.displayGraphIds = []
      this.fetchSurvey()
    },
    /**
     * クロス分析の変更
     * @param  { String } value
     * @return { Void }
     */
    async changeCrossAnalysisAxis(value) {
      if (this.analysisSurveyLoading) return
      this.crossAnalysisAxis = value
      this.displayGraphIds = []
      this.fetchSurvey()
    },
    changePeriod(value) {
      if (this.analysisSurveyLoading) return
      this.individualAnalysisField = null
      this.setPeriod(value)
      this.fetchSurvey()
    },
    toggleGraphDisplayType() {
      this.graphDisplayType = this.graphDisplayType === 'count' ? 'ratio' : 'count'
    },
    calcChartData(option) {
      return this.displayGraphDataList.map(data => {
        const displayQuestion = data.questions.find(question => question.id === this.displayQuestionId)
        const displayOption = displayQuestion.options.find(v => v.id === option.id)
        // 複数選択は件数表示だけ
        if (!this.canDisplayRatio || this.graphDisplayType === 'count') {
          return displayOption.value
        } else {
          return roundDecimal(displayOption.rate * 100, decimalPlace)
        }
      })
    },
    calcNpsChartData(nps) {
      return this.displayGraphDataList.map(graph => {
        const question = graph.questions.find(question => question.id === this.displayQuestionId)
        const npsValue = question.value[nps.id]
        if (this.graphDisplayType === 'count') {
          return npsValue.value
        } else {
          return roundDecimal(npsValue.rate * 100, decimalPlace)
        }
      })
    },
    // TODO:displayGraphDataListを算出プロパティにしたいが、日付選択するとwarningが出てしまう。
    // 原因を要調査
    // https://github.com/movinc/kComApp/pull/6233#discussion_r979544058
    changeDisplayGraph(graphIds) {
      this.displayGraphIds = graphIds
      let graphDataList = []
      graphIds.forEach((item) => {
        this.analysisSurveyDataList.forEach((row) => {
          if(item === row.id) {
            graphDataList.push(row)
          }
        })
      })
      this.displayGraphDataList = graphDataList
    },
    async setExportCsv() {
      await this.exportCsv(this.requestParams)
      this.$router.push({ name: 'CompaniesExportResults' })
    },
    getTooltipLabel(context) {
      const unit = !this.canDisplayRatio || this.graphDisplayType === 'count' ? '' : '%'
      return `${context.dataset.label}: ${context.formattedValue}${unit}`
    },
    handleInputSurveyName({ value }) {
      this.inputSurveyName = value
    },
    handleSuggestFieldClose() {
      if (this.inputSurveyName === '') {
        this.handleInputSurveyName({ value: this.cacheInputSurveyName })
      }
      this.cacheInputSurveyName = ''
    },
    handleSuggestFieldFocus() {
      this.cacheInputSurveyName = this.inputSurveyName
      this.handleInputSurveyName({ value: '' })
    },
  }
}
</script>

<style lang="scss" scoped>
.c-links {
  overflow: visible !important;
}
.graphHeader {
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
}
.displayGraph {
  display: flex;
  align-items: baseline;
}
.changeDisplayGraphTypeBtn {
  margin: 16px 0;
}
.graphs {
  width: 80%;
  margin: 32px auto;
}
.headerButtons {
  margin-top: 6px;
  display: flex;
}

.surveyForm {
  padding: 8px 0px;
  display: flex;
  align-items: center;

  &__label {
    font-weight: bold;
    margin-right: 12px;
  }

  &__input {
    min-width: 200px;
  }
}
.csvButton {
  margin-left: 10px;
}
.statusMark {
  border-radius: 50%;
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 2px;
}
.surveyInfo {
  background: #F5F6F9;
  border: 1px solid #E4E7EB;
  box-shadow: 0px 0px 0px rgba(63, 63, 68, 0.05);
  border-radius: 4px;
  padding: 24px 32px;
  margin-bottom: 40px;
  &__title {
    font-weight: 600;
    font-size: 14px;
  }
  &__status {
    padding-top: 8px;
  }
  &__period {
    display: flex;
    padding-top: 8px;
    @media screen and (max-width: 768px) {
      display: block;
    }
    &--endDate {
      padding-left: 8px;
      @media screen and (max-width: 768px) {
        padding: 0;
      }
    }
  }
  &__total {
    display: flex;
    padding: 16px 0 0 0;
    @media screen and (max-width: 768px) {
      display: block;
    }
  }
  &__preview {
    padding: 0 0 0 16px;
    @media screen and (max-width: 768px) {
      padding: 0;
    }
    &__link {
      color: #e9446a;
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>
